import {
	SiteFragmentFragment,
	type AuthSettings,
	type TeamFragmentFragment,
	type User,
} from '@apps/www/src/__generated__/graphql';
import SVGridAddButtonContainer from '@apps/www/src/www/containers/SVGridAddButtonContainer';
import SVNavSearchContainer from '@apps/www/src/www/containers/SVNavSearchContainer';
import type SVNotificationsIconContainer from '@apps/www/src/www/containers/SVNotificationsIconContainer';
import SVSubscriptionWarningContainer from '@apps/www/src/www/containers/SVSubscriptionWarningContainer';
import SVTrackedUpgradeButtonContainer from '@apps/www/src/www/containers/SVTrackedUpgradeButtonContainer';
import useTeamHomePath from '@apps/www/src/www/hooks/useTeamHomePath';
import { closeMenu } from '@apps/www/src/www/reducers/ui';
import SVA from '@pkgs/shared-client/components/SVA';
import SVActions from '@pkgs/shared-client/components/SVActions';
import SVAvatar from '@pkgs/shared-client/components/SVAvatar';
import SVButton, { SVButtonSIZES, SVButtonUSES } from '@pkgs/shared-client/components/SVButton';
import SVDropdown from '@pkgs/shared-client/components/SVDropdown';
import SVDropdownContent from '@pkgs/shared-client/components/SVDropdownContent';
import SVFlexSpacer from '@pkgs/shared-client/components/SVFlexSpacer';
import SVIconButton from '@pkgs/shared-client/components/SVIconButton';
import SVLink from '@pkgs/shared-client/components/SVLink';
import { USES } from '@pkgs/shared-client/components/SVNotificationIcon';
import SVPageMargins from '@pkgs/shared-client/components/SVPageMargins';
import config from '@pkgs/shared-client/config';
import formatCount from '@pkgs/shared-client/helpers/formatCount';
import matchLocation from '@pkgs/shared-client/helpers/matchLocation';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import ArrowRightToTop from '@pkgs/shared-client/img/arrow-right-to-top-inlined.svg';
import IconCloseSVG from '@pkgs/shared-client/img/icon-close-inlined.svg';
import iconDarkMode from '@pkgs/shared-client/img/icon-dark-mode-inlined.svg';
import IconHappyFaceSVG from '@pkgs/shared-client/img/icon-happy-face-inlined.svg';
import IconLightMode from '@pkgs/shared-client/img/icon-light-mode-inlined.svg';
import IconSearchMenuSVG from '@pkgs/shared-client/img/icon-search-menu-inlined.svg';
import IconSmallSelectSVG from '@pkgs/shared-client/img/icon-select-small-inlined.svg';
import IconSettingsSVG from '@pkgs/shared-client/img/icon-settings-inlined.svg';
import IconSiteMakerSVG from '@pkgs/shared-client/img/icon-site-maker-inlined.svg';
import IconSliderSmallSVG from '@pkgs/shared-client/img/icon-slider-small-inlined.svg';
import IconTeamSVG from '@pkgs/shared-client/img/icon-team-inlined.svg';
import LogoSVG from '@pkgs/shared-client/img/logo-inlined.svg';
import IconLogoSVG from '@pkgs/shared-client/img/logo-s-menu-inlined.svg';
import { unit } from '@pkgs/shared-client/styles/mixins';
import UpgradeButtonSource from '@pkgs/shared/enums/UpgradeButtonSource';
import cn from '@pkgs/shared-client/helpers/cn';
import { NextRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
;

const getUpgradeText = (isExpired: boolean) => (isExpired ? 'Restart Premium' : 'Upgrade');

const _TopSpacer = React.forwardRef<HTMLDivElement>((props, ref) => (
	<div ref={ref} className="h-nav-height" {...props} />
));

type MenuItemsProps = {
	children: React.ReactNode;
	className?: string;
	isLoading?: boolean;
	target?: string;
	onClick?: (event: React.UIEvent<Element, UIEvent>) => void;
	to?: Parameters<NextRouter['push']>[0];
	disableHover?: boolean;
};

const _MenuItems = React.memo(({ children, disableHover = false, ...props }: MenuItemsProps) => {
	const { onClick, ...adjustedProps } = props;

	if (onClick) {
		return (
			<SVDropdownContent.Links.Item
				{...adjustedProps}
				onClick={onClick}
				isOnMenu
				disableHover={disableHover}
			>
				{children}
			</SVDropdownContent.Links.Item>
		);
	}

	return (
		<SVDropdownContent.Links.ItemLink {...adjustedProps} isOnMenu disableHover={disableHover}>
			{children}
		</SVDropdownContent.Links.ItemLink>
	);
});

const _LinkSection = React.memo(({ children }: OnlyChildrenProps) => (
	<div className="flex flex-col px-4">{children}</div>
));

const _NavLogo = React.memo(
	({ isEditing, locationPath, team }: Pick<Props, 'isEditing' | 'locationPath' | 'team'>) => {
		const isCurrent =
			isEditing || matchLocation(locationPath, team?.teamFeedAsHomepage ? '/feed' : '/');

		return (
			<ul>
				<SVIconButton
					src={LogoSVG}
					reversedCurrentStyle={true}
					Component={SVLink}
					to={team?.teamFeedAsHomepage ? '/feed' : '/'}
					title="Home"
					iconClassName={cn(
						'min-h-[24px] w-[24px] h-[24px] min-w-[24px]',
						!isCurrent && 'text-gray-400',
					)}
					isCurrent={isCurrent}
					disableHover
				/>
			</ul>
		);
	},
);

const _NavUpgradeButton = React.memo(
	({
		userHasExpiredSubscription,
		userItemsCount,
	}: Pick<Props, 'userHasExpiredSubscription' | 'userItemsCount'>) => {
		const userItemsCountOrZero = userItemsCount || 0;
		const itemsLimit = config.payment.maxFreeItems;
		const statsText = `You have ${formatCount(
			userItemsCountOrZero,
		)} saves out of your free limit of ${formatCount(itemsLimit)}.`;

		let progressBar: React.ReactNode = null;
		if (userItemsCountOrZero > 0) {
			const ratio = Math.min(1, Math.max(0, userItemsCountOrZero / itemsLimit));

			progressBar = (
				<div className="hidden min-w-[90px] items-center space-y-2 md:flex md:flex-col xl:hidden 2xl:flex">
					<div className="type-label text-primary/75 w-full text-[10px]">
						Saved {formatCount(userItemsCountOrZero)} of {formatCount(itemsLimit)} saves
					</div>
					<div className="bg-primary/20 relative h-[2px] w-full flex-grow overflow-hidden rounded">
						<div
							className={cn(
								'absolute left-0 top-0 h-full',
								ratio >= 0.9 ? 'bg-danger' : 'bg-brand',
							)}
							style={{ width: unit(Math.round(ratio * 100), '%') }}
						/>
					</div>
				</div>
			);
		}

		return (
			<li>
				<div className="relative flex items-center space-x-6">
					{progressBar}
					<SVTrackedUpgradeButtonContainer
						source={UpgradeButtonSource.NAV_BAR}
						size={SVButtonSIZES.SMALL}
						to="/upgrade/"
						className="py-3"
						title={
							userHasExpiredSubscription
								? `${getUpgradeText(true)}\n\n${statsText}`
								: getUpgradeText(false)
						}
					>
						{getUpgradeText(userHasExpiredSubscription)}
					</SVTrackedUpgradeButtonContainer>
				</div>
			</li>
		);
	},
);

const _NavItemLink = React.memo(
	({
		to,
		label,
		Icon,
		side,
		className,
	}: {
		to: string;
		label: string;
		Icon: React.ComponentType;
		side: boolean;
		className?: string;
	}) => (
		<_MenuItems to={to}>
			{side ? (
				<div className="flex w-full items-center space-x-2 pr-1">
					<div className={className}>{label}</div>
					<SVFlexSpacer />
					<div className="text-gray-500">
						<Icon />
					</div>
				</div>
			) : (
				<>
					<div className="mr-2 flex w-[28px] items-center justify-center text-gray-500">
						<Icon />
					</div>
					<div className={className}>{label}</div>
				</>
			)}
		</_MenuItems>
	),
);

const _NavItemLinkAction = React.memo(
	({
		label,
		Icon,
		onClick,
	}: {
		label: string;
		Icon: React.ComponentType;
		onClick: (event: React.UIEvent<Element, UIEvent>) => void;
	}) => (
		<_MenuItems onClick={onClick}>
			<div className="mr-2 flex w-[28px] items-center justify-center text-gray-500">
				<Icon />
			</div>
			{label}
		</_MenuItems>
	),
);

const _NavAvatarIcon = React.memo(
	({
		userURL,
		userName,
		userAvatarURL,
		onToggleDarkMode,
		isDarkMode,
		userEmail,
		team,
		unreadNotificationsCount,
		onToggleEditing,
		showGridControls,
		NotificationsComponent,
		onOpenGridSettings,
		site,
		locationPath,
		userHasExpiredSubscription,
		userHasProPrivileges,
	}: Pick<
		Props,
		| 'userURL'
		| 'userName'
		| 'userAvatarURL'
		| 'onToggleDarkMode'
		| 'isDarkMode'
		| 'userEmail'
		| 'team'
		| 'unreadNotificationsCount'
		| 'onToggleEditing'
		| 'showGridControls'
		| 'NotificationsComponent'
		| 'onOpenGridSettings'
		| 'site'
		| 'locationPath'
		| 'userHasExpiredSubscription'
		| 'userHasProPrivileges'
	>) => (
		<SVDropdown
			positionStrategy={SVDropdown.POSITION_STRATEGIES.FIXED}
			triggerType={SVDropdown.TRIGGER_TYPES.HOVER}
			contentOffset="h-[8px]"
			renderTrigger={({ isOpen, ...props }) => (
				<SVLink to={userURL} title="View your saves" className="relative" {...props}>
					<span
						className={cn(
							'absolute z-10 ml-[50%] mt-[50%] block translate-x-[-50%] translate-y-[-50%] rounded-full',
							'h-full min-h-[40px] w-full min-w-[40px]',
							'hover:bg-gray-50 hover:bg-opacity-10',
						)}
					/>
					<SVAvatar className="h-[28px] w-[28px]" src={userAvatarURL} />
				</SVLink>
			)}
			renderContent={() => (
				<div className="z-index-nav -md:min-w-[220px] isolate min-w-[260px] py-5">
					<_LinkSection>
						<div className="mx-2">
							<SVDropdownContent.Links.ItemLinkMain
								to={userURL}
								label={userEmail}
								disableHover
							>
								<div className="-mb-1">{userName}</div>
							</SVDropdownContent.Links.ItemLinkMain>
						</div>
						{(userHasExpiredSubscription || !userHasProPrivileges) && (
							<div className="-lg:block hidden">
								<SVTrackedUpgradeButtonContainer
									source={UpgradeButtonSource.NAV_MENU}
									className="mt-2 w-full"
									size={SVButtonSIZES.EXTRA_SMALL}
									use={SVButtonUSES.PRIMARY}
									to="/upgrade/"
								>
									{userHasExpiredSubscription ? 'Restart Premium' : 'Upgrade'}
								</SVTrackedUpgradeButtonContainer>
							</div>
						)}
						<SVDropdownContent.Links.Separator />
						<_NavItemLink
							to="/profile/"
							label="Settings"
							Icon={IconSettingsSVG}
							side={false}
						/>
						<_NavItemLink
							to="/site-maker/edit/"
							label={site ? 'My site' : 'Create your site'}
							Icon={IconSiteMakerSVG}
							side={false}
						/>
						<_NavItemLink
							to={team ? `/team/users/` : '/team/'}
							label={team ? 'Team' : 'Start a team'}
							Icon={IconTeamSVG}
							side={false}
						/>
						<_NavItemLink
							to="/extension/"
							label="Get Extension"
							Icon={IconHappyFaceSVG}
							side={false}
						/>
						{onToggleDarkMode && (
							<_NavItemLinkAction
								onClick={onToggleDarkMode}
								Icon={isDarkMode ? IconLightMode : iconDarkMode}
								label={isDarkMode ? 'Light Mode' : 'Dark Mode'}
							/>
						)}
						<div className="-sm:block relative hidden">
							{NotificationsComponent && (
								<NotificationsComponent showLabel use={USES.NAV_AVATAR} />
							)}
						</div>
						{showGridControls && (
							<_NavItemLinkAction
								onClick={onOpenGridSettings}
								label="Customize Grid"
								Icon={IconSliderSmallSVG}
							/>
						)}

						{showGridControls && (
							<_NavItemLinkAction
								onClick={onToggleEditing}
								label="Multi select"
								Icon={IconSmallSelectSVG}
							/>
						)}
						<SVDropdownContent.Links.Separator />
						<_NavItemLink
							to="https://inspire.savee.it/"
							label="Blog"
							Icon={ArrowRightToTop}
							side
							className="pl-1"
						/>
						<_NavItemLink
							to="/marketplace/"
							label="Marketplace"
							Icon={ArrowRightToTop}
							side
							className="pl-1"
						/>
						<_NavItemLink
							to="/course/"
							className="pl-1"
							label="Course"
							side
							Icon={ArrowRightToTop}
						/>
						<_MenuItems to="/contact-us/" className="pl-1">
							Contact us
						</_MenuItems>
						<_MenuItems to="/logout/" className="pl-1">
							Log out
						</_MenuItems>

						<div className="mx-2 flex justify-between pt-10 text-sm text-gray-900">
							<SVA
								Component={SVLink}
								to="/privacy/"
								className="text-gray-400 hover:text-gray-500"
							>
								Privacy
							</SVA>
							<SVA
								Component={SVLink}
								to="/copyright-policy/"
								className="text-gray-400 hover:text-gray-500"
							>
								Copyrights
							</SVA>
							<SVA
								Component={SVLink}
								to="/terms/"
								className="text-gray-400 hover:text-gray-500"
							>
								Terms
							</SVA>
						</div>
					</_LinkSection>
				</div>
			)}
		/>
	),
);

export const SmallNavBottom = React.memo(
	({
		onCloseNav,
		userHasExpiredSubscription,
		userCanSeeBillingPage,
		onToggleDarkMode,
		isDarkMode,
		onOpenGridUpload,
		NotificationsComponent,
		team,
		site,
	}: Pick<
		Props,
		| 'onCloseNav'
		| 'userURL'
		| 'userCanSeeBillingPage'
		| 'userHasExpiredSubscription'
		| 'isDarkMode'
		| 'onToggleDarkMode'
		| 'onOpenGridUpload'
		| 'NotificationsComponent'
		| 'team'
		| 'site'
	>) => {
		const dispatch = useDispatch();

		const handleClose = useEventCallback(() => {
			dispatch(closeMenu());
		});

		return (
			<div className="z-index-notifications first-letter:type-subtitle border-l-separator -smp:w-[320px] bg-background/40 relative flex h-full w-[356px] flex-shrink flex-grow flex-col items-center overflow-y-auto border-l pl-12 pr-8 backdrop-blur-md">
				<div className="z-index-dropdown  absolute right-8 top-14 flex items-center p-4">
					<SVIconButton
						onClick={(e) => {
							e.stopPropagation();
							handleClose();
						}}
						src={IconCloseSVG}
						label="Close"
						keys="escape"
						className="z-index-dropdown pointer-events-auto"
					/>
				</div>
				<ul className="mb-11 mt-14 flex w-full flex-shrink flex-grow flex-col">
					{!userCanSeeBillingPage || userHasExpiredSubscription ? (
						<SVButton
							className="mb-4 h-12 w-full text-xl"
							Component={SVLink}
							to={userHasExpiredSubscription ? '/billing/' : '/upgrade/'}
							title={userHasExpiredSubscription ? 'Restart Premium' : 'Upgrade'}
							size={SVButtonSIZES.EXTRA_SMALL}
						>
							{userHasExpiredSubscription ? 'Restart Premium' : 'Upgrade'}
						</SVButton>
					) : null}
					<_SmallNavItemLink link="/profile/">Settings</_SmallNavItemLink>
					<_SmallNavItemLink link="/site-maker/edit/">
						{site ? 'My site' : 'Create your site'}
					</_SmallNavItemLink>
					<_SmallNavItemLink link={team ? `/team/users/` : '/team/'}>
						{team ? 'Team' : 'Start a team'}
					</_SmallNavItemLink>
					{NotificationsComponent && (
						<NotificationsComponent showLabel use={USES.MENU_PROFILE} />
					)}
					{onToggleDarkMode && (
						<_SmallNavItemLink onClick={onToggleDarkMode}>
							{isDarkMode ? 'Light Mode' : 'Dark Mode'}
						</_SmallNavItemLink>
					)}
					<_SmallNavItemLink link="/" onClick={onOpenGridUpload}>
						Upload
					</_SmallNavItemLink>
					<SVDropdownContent.Links.Separator />
					<_SmallNavItemExternalLink
						link="https://inspire.savee.it/"
						Icon={ArrowRightToTop}
						useIcon
					>
						Blog
					</_SmallNavItemExternalLink>
					<_SmallNavItemExternalLink link="/course/" Icon={ArrowRightToTop} useIcon>
						Marketplace
					</_SmallNavItemExternalLink>
					<_SmallNavItemExternalLink link="/course/" Icon={ArrowRightToTop} useIcon>
						Course
					</_SmallNavItemExternalLink>
					<SVDropdownContent.Links.Separator />
					<_SmallNavItemLink link="/contact-us/">Contact us</_SmallNavItemLink>
					<_SmallNavItemLink link="/logout/">Log out</_SmallNavItemLink>
				</ul>
				<div className=" w-full">
					<div className="type-small mb-10 max-w-[164px] text-gray-400">
						Made with love by immigrants in America{' '}
					</div>
				</div>
			</div>
		);
	},
);

const _DefaultNavItems = React.memo(
	({
		locationPath,
		items,
		side = 'left',
	}: {
		locationPath: string;
		items: { to: string; label: string; matchUrl?: string; target?: string }[];
		side?: 'left' | 'right';
	}) => {
		const navgateToTop = (e, item) => {
			const itemPath = item.to;

			const matchPattern = item.matchUrl || `${itemPath}/**`;

			if (matchLocation(locationPath, matchPattern)) {
				if (e) e.preventDefault();

				if (matchLocation(locationPath, itemPath)) {
					window.scrollTo({ top: 0, behavior: 'smooth' });
					return;
				}

				window.location.href = itemPath;
				return;
			}
		};

		const subnavItems = items.map((item) => (
			<SVActions.NavItem
				key={item.to}
				to={item.to}
				matchURL={item.matchUrl}
				target={item.target}
			>
				<div
					className={cn(
						'whitespace-no-wrap -md:max-w-[8.8vw] max-w-[12vw] cursor-pointer truncate',
						side === 'right' && 'type-base',
					)}
					onClick={(e) => navgateToTop(e, item)}
				>
					{item.label}
				</div>
			</SVActions.NavItem>
		));

		return <SVActions use={SVActions.USES.FEEDNAV}>{subnavItems}</SVActions>;
	},
);

const _Nav = ({
	isDarkMode,
	isEditing,
	isLogged,
	// isNavOpen,
	locationPath,
	// onCloseNav,
	onOpenGridSettings,
	// onOpenNav,
	// onOpenOnboard,
	// onSelectFiles,
	onToggleDarkMode,
	onToggleEditing,
	showUpgrade,
	userAvatarURL,
	userHasProPrivileges,
	userHasMarketplace,
	userHasExpiredSubscription,
	NotificationsComponent,
	showGridControls,
	userURL,
	userName,
	userItemsCount,
	userEmail,
	unreadNotificationsCount,
	onHeightChange,
	team,
	site,
}: Omit<Props, 'isSmallNav'>) => {
	const divRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (typeof ResizeObserver === 'undefined') {
			return;
		}

		const handleResize = () => {
			onHeightChange && onHeightChange(divRef.current?.clientHeight || 0);
		};

		const resizeObserver = new ResizeObserver(handleResize);

		if (divRef.current) {
			resizeObserver.observe(divRef.current);
		}

		const currentRef = divRef.current;

		return () => {
			if (currentRef) {
				resizeObserver.unobserve(currentRef);
			}
		};
	}, [onHeightChange]);

	const teamHomePath = useTeamHomePath();

	const commonNavItems: {
		to: string;
		label: string;
		matchUrl?: string;
	}[] = [
		team && {
			to: teamHomePath,
			label: team.name,
			matchUrl: '/team/**', // TODO: If `team.teamFeedAsHomepage` is `true`, we should match against `/` as well
		},
		{
			to: '/explore/',
			label: 'Explore',
		},
	].filter(Boolean);

	const desktopNavItems: {
		to: string;
		label: string;
		matchUrl?: string;
	}[] = [
		{
			to: userURL,
			label: 'You',
			matchUrl: `${userURL}**`,
		},
		...commonNavItems,
	].filter(Boolean);

	const rightActionsItems: {
		to: string;
		label: string;
		matchUrl?: string;
		target?: string;
	}[] = [
		{
			to: userHasMarketplace ? '/marketplace/' : `https://marketplace.savee.it/`,
			label: 'Marketplace',
			target: userHasMarketplace ? '_self' : '_blank',
		},
	];

	const smallRightActionsItems: {
		to: string;
		label: string;
		matchUrl?: string;
		target?: string;
	}[] = [...rightActionsItems, { to: '/course/', label: 'Course', matchUrl: '/course/**' }];

	return (
		<div ref={divRef}>
			<SVSubscriptionWarningContainer />
			<div className="h-nav-height text-secondary relative flex items-center">
				<div className="duration-slide bg-background absolute inset-0 h-full w-full transition-colors ease-in-out" />
				<SVPageMargins className="flex-grow">
					<ul className="flex items-center space-x-6">
						<_NavLogo isEditing={isEditing} locationPath={locationPath} team={team} />
						{isLogged ? (
							<>
								<div className="-md:hidden block">
									<_DefaultNavItems
										items={desktopNavItems}
										locationPath={locationPath}
									/>
								</div>
								<div className="-md:block hidden">
									<_DefaultNavItems
										items={commonNavItems}
										locationPath={locationPath}
									/>
								</div>
								<SVNavSearchContainer />
							</>
						) : null}
						<SVFlexSpacer />
						{isLogged &&
							showUpgrade &&
							!userHasExpiredSubscription &&
							!userHasProPrivileges && (
								<div className="-lg:hidden block">
									<_NavUpgradeButton
										userHasExpiredSubscription={userHasExpiredSubscription}
										userItemsCount={userItemsCount}
									/>
								</div>
							)}
						{isLogged ? (
							<>
								<div className="-lg:hidden block">
									<_DefaultNavItems
										items={smallRightActionsItems}
										locationPath={locationPath}
										side="right"
									/>
								</div>
								<div className="-lg:block -md:hidden hidden">
									<_DefaultNavItems
										items={rightActionsItems}
										locationPath={locationPath}
										side="right"
									/>
								</div>
							</>
						) : null}
						<li>
							<ul className="flex items-center space-x-6">
								<div className="-sm:hidden flex items-center space-x-6 text-gray-400">
									<div>
										{isLogged && (
											<SVGridAddButtonContainer iconClassName="text-gray-400" />
										)}
									</div>

									{NotificationsComponent && (
										<div className="relative">
											<NotificationsComponent />
										</div>
									)}
								</div>

								{isLogged && (
									<_NavAvatarIcon
										userURL={userURL}
										userName={userName}
										userAvatarURL={userAvatarURL}
										onToggleDarkMode={onToggleDarkMode}
										isDarkMode={isDarkMode}
										userEmail={userEmail}
										team={team}
										unreadNotificationsCount={unreadNotificationsCount}
										onToggleEditing={onToggleEditing}
										showGridControls={showGridControls}
										NotificationsComponent={NotificationsComponent}
										onOpenGridSettings={onOpenGridSettings}
										site={site}
										locationPath={locationPath}
										userHasExpiredSubscription={userHasExpiredSubscription}
										userHasProPrivileges={userHasProPrivileges}
									/>
								)}
								{!isLogged && (
									<li className="color-white z-10 ml-3">
										<SVA Component={SVLink} to="/join/">
											Join
										</SVA>{' '}
										/{' '}
										<SVA Component={SVLink} to="/login/">
											Log In
										</SVA>
									</li>
								)}
							</ul>
						</li>
					</ul>
				</SVPageMargins>
			</div>
		</div>
	);
};

const _SmallNavItem = React.memo(
	({
		index,
		isSelected,
		onClick,
		onSetRef,
		className = 'flex w-11 justify-center',
		children,
	}: {
		index: number;
		isSelected: boolean;
		onClick: () => void;
		onSetRef: (el: HTMLDivElement | null) => void;
		className?: string;
		children: React.ReactNode;
	}) => (
		<div
			ref={onSetRef}
			className={`${className} relative z-10 cursor-pointer`}
			onClick={onClick}
		>
			{children}
		</div>
	),
);

const _SmallNav = ({
	// isDarkMode,
	// isEditing,
	// showGridControls,
	isLogged,
	// isNavOpen,
	locationPath,
	//onCloseNav,
	// onOpenGridSettings,
	// onOpenGridUpload,
	// onOpenNav,
	// onOpenOnboard,
	// onSelectFiles,
	// onToggleDarkMode,
	NotificationsComponent,
	// onToggleEditing,
	// showUpgrade,
	// userAvatarURL,
	// userCanSeeBillingPage,
	// userHasExpiredSubscription,
	userURL,
	// team,
	// site,
	userAvatarURL,
	userName,
}: Omit<Props, 'isSmallNav'>) => {
	const [selected, setSelected] = useState(0);
	const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 44 });
	const containerRef = useRef<HTMLDivElement>(null);
	const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

	const handleSelect = useEventCallback((index: number) => {
		if (navItems[index].hasEspecialMethod) return;
		setSelected(index);
		updateIndicatorPosition(index);
	});

	const updateIndicatorPosition = useEventCallback((index: number) => {
		const container = containerRef.current;
		const item = itemRefs.current[index];

		if (!container || !item) {
			return;
		}

		const containerRect = container.getBoundingClientRect();
		const itemRect = item.getBoundingClientRect();

		// Calculate the center position of the item relative to container
		const itemCenter = itemRect.left + itemRect.width / 2 - containerRect.left;
		const width = index === 4 ? 32 : 44; // Avatar is smaller (h-8 w-8 = 32px), others are w-11 = 44px
		const left = itemCenter - width / 2; // Center the indicator on the item

		setIndicatorStyle({ left, width });
	});

	const routes = ['/', '/search/**', '/notifications', userURL];

	// Navigation items configuration
	const navItems = [
		{
			component: (
				<SVIconButton
					src={IconLogoSVG}
					reversedCurrentStyle={true}
					Component={SVLink}
					to="/"
					title="Home"
					className={selected === 0 ? 'text-gray-900' : 'text-gray-300'}
					disableHover
				/>
			),
		},
		{
			component: (
				<SVIconButton
					Component={SVLink}
					src={IconSearchMenuSVG}
					to="/search/"
					label="Search"
					className={selected === 1 ? 'text-gray-800' : 'stroke-width="2" text-gray-300'}
					iconClassName="w-full"
					disableHover
				/>
			),
		},
		{
			component: (
				<SVGridAddButtonContainer
					iconClassName={selected === 2 ? 'text-gray-800' : 'text-gray-300'}
					isSmallNav={true}
					disableHover
				/>
			),
			hasEspecialMethod: true,
		},
		{
			component: NotificationsComponent ? (
				<NotificationsComponent showLabel={false} use={USES.MENU_SMALL} />
			) : null,
			className: 'flex justify-center w-11',
			hasEspecialMethod: true,
		},
		{
			component: (
				<SVA Component={SVLink} to={userURL}>
					<SVAvatar src={userAvatarURL} title={userName} className="h-7 w-7" />
				</SVA>
			),
		},
	];

	useEffect(() => {
		routes.forEach((route, index) => {
			if (matchLocation(locationPath, route)) {
				const selectedIndex = index > 1 ? index + 1 : index;
				setSelected(selectedIndex);
				// Use requestAnimationFrame for better DOM synchronization
				requestAnimationFrame(() => updateIndicatorPosition(selectedIndex));
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [locationPath, updateIndicatorPosition]);

	// Update indicator position when component mounts or resizes
	useEffect(() => {
		const handleResize = () => {
			updateIndicatorPosition(selected);
		};

		// Update position after mount with better timing
		requestAnimationFrame(() => updateIndicatorPosition(selected));

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [selected, updateIndicatorPosition]);

	// TODO: Need to create the unlogged nav to small nav
	if (!isLogged) {
		return null;
	}

	return (
		<ul className="theme-dark relative flex h-full w-full items-center justify-center">
			<div className="from-background absolute inset-0 w-full bg-gradient-to-t to-transparent" />

			<div
				ref={containerRef}
				className="relative m-6 mb-8 flex h-[60px] w-[256px] min-w-[256px] max-w-[256px] items-center justify-evenly rounded-full bg-white bg-opacity-10 pl-0.5 pr-1 shadow-xl backdrop-blur-xl"
			>
				<div
					className="absolute rounded-full bg-gray-50 transition-all duration-300 ease-in-out"
					style={{
						left: `${indicatorStyle.left}px`,
						width: `${indicatorStyle.width}px`,
						height: `${indicatorStyle.width}px`,
						top: '50%',
						transform: 'translateY(-50%)',
					}}
				/>

				{navItems.map(
					(item, index) =>
						item.component && (
							<_SmallNavItem
								key={index}
								index={index}
								isSelected={selected === index}
								onClick={() => handleSelect(index)}
								onSetRef={(el) => (itemRefs.current[index] = el)}
								className={item.className}
							>
								{item.component}
							</_SmallNavItem>
						),
				)}
			</div>
		</ul>
	);
};

const _SmallNavItemLink = ({
	children,
	link,
	onClick,
}: {
	children: React.ReactNode;
	link?: string;
	onClick?: (event: React.UIEvent) => void;
}) => (
	<_MenuItems
		to={link}
		onClick={onClick}
		className="type-subtitle my-2.5 items-center"
		disableHover
	>
		{children}
	</_MenuItems>
);

const _SmallNavItemExternalLink = ({
	children,
	link,
	onClick,
	Icon,
	useIcon,
}: {
	children: React.ReactNode;
	link: string;
	onClick?: (event: React.UIEvent) => void;
	Icon?: React.ComponentType;
	useIcon?: boolean;
}) => (
	<_MenuItems
		to={link}
		target="_blank"
		onClick={onClick}
		className="type-subtitle my-2.5 justify-between"
		disableHover
	>
		{children}
		{useIcon && <div className="ml-3 text-gray-600">{Icon && <Icon />}</div>}
	</_MenuItems>
);

type Props = {
	isSmallNav: boolean;
	isDarkMode: AuthSettings['darkMode'];
	isEditing: boolean;
	showGridControls: boolean;
	isLogged: boolean;
	isAtTop: boolean;
	isNavOpen: boolean;
	locationPath: string;
	onCloseNav: (event: React.UIEvent) => void;
	onOpenGridSettings: (event: React.UIEvent) => void;
	onOpenGridUpload: (event: React.UIEvent) => void;
	onOpenNav: (event: React.UIEvent) => void;
	onNewBoardClick: (event: React.UIEvent) => void;
	onOpenOnboard: (event: React.UIEvent) => void;
	onSelectFiles: (files: FileList) => void;
	onToggleDarkMode?: (event: React.UIEvent) => void;
	onToggleEditing: (event: React.UIEvent) => void;
	showUpgrade: boolean;
	userAvatarURL: User['avatarURL'] | null;
	userCanSeeBillingPage: boolean;
	userHasProPrivileges: boolean;
	userHasMarketplace: boolean;
	userHasExpiredSubscription: boolean;
	userURL: User['url'];
	userName: User['name'];
	userItemsCount: User['itemsCount'] | null;
	NotificationsComponent?: typeof SVNotificationsIconContainer;
	team?: TeamFragmentFragment | null;
	site: SiteFragmentFragment | null;
	userEmail: AuthSettings['email'];
	unreadNotificationsCount: AuthSettings['unreadNotificationsCount'];
	onHeightChange: (height: number) => void;
};

const SVNav = ({ isSmallNav, ...otherProps }: Props) => {
	if (isSmallNav) {
		return <_SmallNav {...otherProps} />;
	}

	return <_Nav {...otherProps} />;
};

SVNav.TopSpacer = _TopSpacer;

SVNav.Avatar = _NavAvatarIcon;

SVNav.NavLogo = _NavLogo;

export default SVNav;
